import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  adminGetCustomers,
  adminGetCustomer,
  adminCreateCustomer,
  adminUpdateCustomer,
  adminDeleteCustomer,
  adminAddPackageToCustomer,
  adminResendCustomerOnboardingInvite,
  adminGetCustomerAgents,
  adminGetPackages
} from '../../lib/api';
import { formatCurrency, formatDateTime, formatMinutes } from '../../lib/utils';
import { toast } from 'sonner';
import {
  Users,
  Plus,
  ChevronRight,
  ChevronLeft,
  ArrowLeft,
  Mail,
  Phone,
  Building2,
  MapPin,
  Calendar,
  CreditCard,
  PhoneCall,
  Clock,
  DollarSign,
  Edit,
  Trash2,
  Package,
  Bot,
  Loader2,
  Search,
} from 'lucide-react';

export function AdminCustomersPage() {
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeletedCustomers, setShowDeletedCustomers] = useState(false);
  const limit = 20;

  useEffect(() => {
    loadCustomers();
  }, [page]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await adminGetCustomers(limit, page * limit);
      setCustomers(response.data.customers);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const visibleCustomers = showDeletedCustomers
    ? filteredCustomers
    : filteredCustomers.filter((customer) => (customer.status || 'active') !== 'deleted');

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-customers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-jakarta font-bold text-slate-900">Customers</h1>
          <p className="text-slate-500">Manage your customer base and their agents</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} data-testid="add-customer-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Customer
        </Button>
      </div>

      <Card className="border-0 shadow-md">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 max-w-sm"
                data-testid="customer-search"
              />
            </div>
            <div className="flex items-center gap-2 h-10 px-3 border border-slate-200 rounded-md bg-white">
              <Checkbox
                id="show-deleted-customers"
                checked={showDeletedCustomers}
                onCheckedChange={(checked) => setShowDeletedCustomers(checked === true)}
              />
              <Label htmlFor="show-deleted-customers" className="text-sm text-slate-600 cursor-pointer">
                Show deleted customers
              </Label>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-32 mb-2" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                </div>
              ))}
            </div>
          ) : visibleCustomers.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900">No customers found</h3>
              <p className="text-slate-500 mb-4">
                {searchTerm || showDeletedCustomers
                  ? 'Try a different search term or filter'
                  : 'Add your first customer to get started'}
              </p>
              {!searchTerm && (
                <Button onClick={() => setShowCreateModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Customer
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Agents</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleCustomers.map((customer) => (
                    <TableRow key={customer.user_id} data-testid={`customer-row-${customer.user_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="w-9 h-9">
                            <AvatarImage src={customer.picture} />
                            <AvatarFallback className="bg-blue-100 text-blue-700 text-sm">
                              {customer.name?.charAt(0) || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-slate-900">{customer.name}</p>
                            <p className="text-sm text-slate-500">{customer.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {customer.company_name ? (
                          <span className="text-slate-900">{customer.company_name}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-900">{customer.agent_count || 0}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={customer.status === 'active' ? 'default' : 'secondary'}>
                          {customer.status || 'active'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/admin/customers/${customer.user_id}`}>
                            View
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-slate-100">
                  <p className="text-sm text-slate-500">
                    Showing {visibleCustomers.length} customers on this page ({total} total)
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p - 1)}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600 px-2">
                      Page {page + 1} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p + 1)}
                      disabled={page >= totalPages - 1}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <CreateCustomerModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          loadCustomers();
        }}
      />
    </div>
  );
}

function CreateCustomerModal({ open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    abn: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postcode: '',
    country: 'Australia',
    initial_minutes: 0,
    assign_package_id: ''
  });

  useEffect(() => {
    if (open) {
      loadPackages();
    }
  }, [open]);

  const loadPackages = async () => {
    try {
      const response = await adminGetPackages();
      setPackages(response.data.packages || []);
    } catch (error) {
      console.error('Failed to load packages');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminCreateCustomer(formData);
      toast.success('Customer created successfully');
      setFormData({
        email: '',
        password: '',
        name: '',
        company_name: '',
        abn: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        postcode: '',
        country: 'Australia',
        initial_minutes: 0,
        assign_package_id: ''
      });
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Customer</DialogTitle>
          <DialogDescription>Create a new customer account with company details</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-6 py-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="customer-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="customer-email-input"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="customer-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+61..."
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-4">Company Details</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="abn">ABN</Label>
                  <Input
                    id="abn"
                    value={formData.abn}
                    onChange={(e) => setFormData({ ...formData, abn: e.target.value })}
                    placeholder="12 345 678 901"
                  />
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Select
                  value={formData.state}
                  onValueChange={(value) => setFormData({ ...formData, state: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NSW">NSW</SelectItem>
                    <SelectItem value="VIC">VIC</SelectItem>
                    <SelectItem value="QLD">QLD</SelectItem>
                    <SelectItem value="WA">WA</SelectItem>
                    <SelectItem value="SA">SA</SelectItem>
                    <SelectItem value="TAS">TAS</SelectItem>
                    <SelectItem value="ACT">ACT</SelectItem>
                    <SelectItem value="NT">NT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="postcode">Postcode</Label>
                <Input
                  id="postcode"
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-4">Initial Package (Optional)</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="assign_package">Assign Package</Label>
                  <Select
                    value={formData.assign_package_id || 'none'}
                    onValueChange={(value) => setFormData({ ...formData, assign_package_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select package (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {packages.filter(p => p.is_active).map((pkg) => (
                        <SelectItem key={pkg.package_id} value={pkg.package_id}>
                          {pkg.name} - {pkg.minutes} mins (${pkg.price})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="initial_minutes">Or Grant Custom Minutes</Label>
                  <Input
                    id="initial_minutes"
                    type="number"
                    min="0"
                    value={formData.initial_minutes}
                    onChange={(e) => setFormData({ ...formData, initial_minutes: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} data-testid="create-customer-submit">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Customer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AdminCustomerDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [agents, setAgents] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddPackageModal, setShowAddPackageModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [resendingInvite, setResendingInvite] = useState(false);

  useEffect(() => {
    loadCustomer();
    loadAgents();
  }, [userId]);

  const loadCustomer = async () => {
    try {
      const response = await adminGetCustomer(userId);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load customer');
      navigate('/admin/customers');
    } finally {
      setLoading(false);
    }
  };

  const loadAgents = async () => {
    try {
      const response = await adminGetCustomerAgents(userId);
      setAgents(response.data.agents || []);
    } catch (error) {
      console.error('Failed to load agents');
    }
  };

  const handleDelete = async () => {
    try {
      await adminDeleteCustomer(userId);
      toast.success('Customer deleted');
      navigate('/admin/customers');
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const handleResendOnboardingInvite = async () => {
    if (!customer?.user_id) return;
    try {
      setResendingInvite(true);
      const response = await adminResendCustomerOnboardingInvite(customer.user_id, {
        user_id: customer.user_id,
      });
      const payload = response?.data || {};
      if (payload.email_queued === false) {
        toast.error('Invite token refreshed but email was not queued. Please retry.');
        return;
      }
      toast.success('Onboarding invite sent with a new token.');
      await loadCustomer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend onboarding invite');
    } finally {
      setResendingInvite(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid md:grid-cols-2 gap-6">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { customer, subscription, packages: customerPackages, calls, payments } = data;
  const canResendOnboardingInvite =
    customer.status === 'pending_activation' ||
    customer.status === 'invited' ||
    customer.onboarding_status === 'pending';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-customer-detail">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/customers')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Avatar className="w-16 h-16">
            <AvatarImage src={customer.picture} />
            <AvatarFallback className="bg-blue-100 text-blue-700 text-xl">
              {customer.name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-2xl font-jakarta font-bold text-slate-900">{customer.name}</h1>
            {customer.company_name && (
              <p className="text-slate-500">{customer.company_name}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canResendOnboardingInvite && (
            <Button
              variant="outline"
              onClick={handleResendOnboardingInvite}
              disabled={resendingInvite}
              data-testid="resend-onboarding-invite-btn"
            >
              {resendingInvite ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Resend Invite
                </>
              )}
            </Button>
          )}
          <Button variant="outline" onClick={() => setShowEditModal(true)} data-testid="edit-customer-btn">
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)} data-testid="delete-customer-btn">
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="agents">Agents ({agents.length})</TabsTrigger>
          <TabsTrigger value="packages">Packages</TabsTrigger>
          <TabsTrigger value="calls">Call History</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span>{customer.email}</span>
                </div>
                {customer.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{customer.phone}</span>
                  </div>
                )}
                {customer.company_name && (
                  <div className="flex items-center gap-3">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <div>
                      <p>{customer.company_name}</p>
                      {customer.abn && <p className="text-sm text-slate-500">ABN: {customer.abn}</p>}
                    </div>
                  </div>
                )}
                {(customer.address || customer.city) && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span>
                      {[customer.address, customer.city, customer.state, customer.postcode]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span>Joined {formatDateTime(customer.created_at)}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Current Subscription</CardTitle>
              </CardHeader>
              <CardContent>
                {subscription ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Badge variant="default" className="text-base px-3 py-1">
                        {subscription.plan_name}
                      </Badge>
                      <span className="text-2xl font-bold text-slate-900">
                        ${subscription.price}/mo
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                      <div>
                        <p className="text-sm text-slate-500">Messages Remaining</p>
                        <p className="text-xl font-semibold text-slate-900">
                          {subscription.remaining_message_count ?? subscription.minutes_remaining}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Messages Used</p>
                        <p className="text-xl font-semibold text-slate-900">
                          {subscription.used_message_count ?? subscription.minutes_used}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No active subscription</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">AI Agents</CardTitle>
              <Button size="sm" asChild>
                <Link to={`/admin/agents/new?customer=${userId}`}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Agent
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {agents.length === 0 ? (
                <div className="text-center py-8">
                  <Bot className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No agents configured</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {agents.slice(0, 3).map((agent) => (
                    <div
                      key={agent.agent_id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <Bot className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{agent.name}</p>
                          <p className="text-sm text-slate-500 capitalize">{agent.industry}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={agent.status === 'active' ? 'default' : 'secondary'}>
                          {agent.status}
                        </Badge>
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/admin/agents/${agent.agent_id}`}>
                            <ChevronRight className="w-4 h-4" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  ))}
                  {agents.length > 3 && (
                    <Button variant="outline" className="w-full" asChild>
                      <Link to={`/admin/agents?customer=${userId}`}>
                        View All {agents.length} Agents
                      </Link>
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="agents">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">AI Agents</CardTitle>
                <CardDescription>Manage voice agents for this customer</CardDescription>
              </div>
              <Button asChild>
                <Link to={`/admin/agents/new?customer=${userId}`}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Agent
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {agents.length === 0 ? (
                <div className="text-center py-12">
                  <Bot className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900">No agents yet</h3>
                  <p className="text-slate-500 mb-4">Create the first agent for this customer</p>
                  <Button asChild>
                    <Link to={`/admin/agents/new?customer=${userId}`}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Agent
                    </Link>
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agent</TableHead>
                      <TableHead>Industry</TableHead>
                      <TableHead>Subscription</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agents.map((agent) => (
                      <TableRow key={agent.agent_id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                              <Bot className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{agent.name}</p>
                              {agent.phone_number && (
                                <p className="text-sm text-slate-500">{agent.phone_number}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="capitalize">{agent.industry}</span>
                        </TableCell>
                        <TableCell>
                          {agent.subscription ? (
                            <Badge>{agent.subscription.plan_name}</Badge>
                          ) : (
                            <Badge variant="secondary">No Plan</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={agent.status === 'active' ? 'default' : 'secondary'}>
                            {agent.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/admin/agents/${agent.agent_id}`}>
                              View
                              <ChevronRight className="w-4 h-4 ml-1" />
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="packages">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Minute Packages</CardTitle>
                <CardDescription>Customer's purchased and granted packages</CardDescription>
              </div>
              <Button onClick={() => setShowAddPackageModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Package
              </Button>
            </CardHeader>
            <CardContent>
              {customerPackages?.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900">No packages</h3>
                  <p className="text-slate-500 mb-4">Add a minute package for this customer</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Package</TableHead>
                      <TableHead>Minutes</TableHead>
                      <TableHead>Remaining</TableHead>
                      <TableHead>Expires</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customerPackages?.map((pkg) => (
                      <TableRow key={pkg.user_package_id}>
                        <TableCell className="font-medium">{pkg.package_name}</TableCell>
                        <TableCell>{pkg.minutes_total}</TableCell>
                        <TableCell>{pkg.minutes_remaining}</TableCell>
                        <TableCell>{formatDateTime(pkg.expires_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calls">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Calls</CardTitle>
            </CardHeader>
            <CardContent>
              {calls?.length === 0 ? (
                <div className="text-center py-12">
                  <PhoneCall className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900">No calls yet</h3>
                  <p className="text-slate-500">Call history will appear here</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Minutes</TableHead>
                      <TableHead>Cost</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {calls?.map((call) => (
                      <TableRow key={call.call_id}>
                        <TableCell>{formatDateTime(call.created_at)}</TableCell>
                        <TableCell>{Math.floor(call.duration_seconds / 60)}:{String(call.duration_seconds % 60).padStart(2, '0')}</TableCell>
                        <TableCell>{call.minutes_used}</TableCell>
                        <TableCell>{formatCurrency(call.cost)}</TableCell>
                        <TableCell>
                          <Badge variant={call.status === 'completed' ? 'default' : 'secondary'}>
                            {call.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Payment History</CardTitle>
            </CardHeader>
            <CardContent>
              {payments?.length === 0 ? (
                <div className="text-center py-12">
                  <DollarSign className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900">No payments yet</h3>
                  <p className="text-slate-500">Payment history will appear here</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments?.map((payment) => (
                      <TableRow key={payment.transaction_id}>
                        <TableCell>{formatDateTime(payment.created_at)}</TableCell>
                        <TableCell className="capitalize">{payment.item_type}</TableCell>
                        <TableCell>{formatCurrency(payment.amount)}</TableCell>
                        <TableCell>
                          <Badge variant={payment.payment_status === 'paid' ? 'default' : 'secondary'}>
                            {payment.payment_status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Modal */}
      <EditCustomerModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        customer={customer}
        onSuccess={() => {
          setShowEditModal(false);
          loadCustomer();
        }}
      />

      {/* Add Package Modal */}
      <AddPackageModal
        open={showAddPackageModal}
        onClose={() => setShowAddPackageModal(false)}
        userId={userId}
        onSuccess={() => {
          setShowAddPackageModal(false);
          loadCustomer();
        }}
      />

      {/* Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Customer</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {customer.name}? This will also deactivate all their agents.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditCustomerModal({ open, onClose, customer, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        company_name: customer.company_name || '',
        abn: customer.abn || '',
        phone: customer.phone || '',
        address: customer.address || '',
        city: customer.city || '',
        state: customer.state || '',
        postcode: customer.postcode || '',
        country: customer.country || 'Australia',
        status: customer.status || 'active',
        new_password: ''
      });
    }
  }, [customer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const updateData = { ...formData };
      if (!updateData.new_password) delete updateData.new_password;
      await adminUpdateCustomer(customer.user_id, updateData);
      toast.success('Customer updated successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update customer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Customer</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>ABN</Label>
                <Input
                  value={formData.abn}
                  onChange={(e) => setFormData({ ...formData, abn: e.target.value })}
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>State</Label>
                <Select
                  value={formData.state}
                  onValueChange={(value) => setFormData({ ...formData, state: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NSW">NSW</SelectItem>
                    <SelectItem value="VIC">VIC</SelectItem>
                    <SelectItem value="QLD">QLD</SelectItem>
                    <SelectItem value="WA">WA</SelectItem>
                    <SelectItem value="SA">SA</SelectItem>
                    <SelectItem value="TAS">TAS</SelectItem>
                    <SelectItem value="ACT">ACT</SelectItem>
                    <SelectItem value="NT">NT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Postcode</Label>
                <Input
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>New Password (leave blank to keep current)</Label>
              <Input
                type="password"
                value={formData.new_password}
                onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                placeholder="••••••••"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddPackageModal({ open, onClose, userId, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [formData, setFormData] = useState({
    package_id: '',
    minutes: 0,
    custom_name: ''
  });

  useEffect(() => {
    if (open) {
      loadPackages();
      setFormData({ package_id: '', minutes: 0, custom_name: '' });
    }
  }, [open]);

  const loadPackages = async () => {
    try {
      const response = await adminGetPackages();
      setPackages(response.data.packages || []);
    } catch (error) {
      console.error('Failed to load packages');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminAddPackageToCustomer(userId, formData);
      toast.success('Package added successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add package');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Package</DialogTitle>
          <DialogDescription>Add a minute package to this customer</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Package</Label>
              <Select
                value={formData.package_id || 'custom'}
                onValueChange={(value) => setFormData({ ...formData, package_id: value === 'custom' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a package" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom Minutes</SelectItem>
                  {packages.filter(p => p.is_active).map((pkg) => (
                    <SelectItem key={pkg.package_id} value={pkg.package_id}>
                      {pkg.name} - {pkg.minutes} mins (${pkg.price})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!formData.package_id && (
              <>
                <div className="space-y-2">
                  <Label>Custom Minutes</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.minutes}
                    onChange={(e) => setFormData({ ...formData, minutes: parseInt(e.target.value) || 0 })}
                    required={!formData.package_id}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Package Name</Label>
                  <Input
                    value={formData.custom_name}
                    onChange={(e) => setFormData({ ...formData, custom_name: e.target.value })}
                    placeholder="e.g., Bonus Minutes"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add Package
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
